import Uri from 'jsuri';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { SECURE_BASE_URL } from '../../config';
import { getConfigRedirectURL } from '../../redux/selectors/config';
import { getIsNewUserFastPlayable, getLaunchRedirect } from '../../redux/selectors/launch';
import { redirectToExternalURL, safeRedirectURL } from '../../utils/router';
import useLatest from '../../web-shared/hooks/use-latest';
import useAppStore from '../use-app-store';
import useRedirectURLParam from './use-redirect-url-param';

export const getDefaultRedirectURL = () => SECURE_BASE_URL;

/** KU
 *
 * Returns a handler, {@link handleRedirectToPlayer}, which redirects the user
 * to a `/player` URL (via {@link redirectToExternalURL}). Depending on the app state,
 * this URL may be derived from {@link useRedirectURLParam},
 * {@link getConfigRedirectURL}, or {@link getLaunchRedirect}. If none of these
 * exist, {@link handleRedirectToPlayer} will redirect to a specified
 * `fallbackExternalURL`. If the selected URL isn't safe to redirect to,
 * {@link handleRedirectToPlayer} redirects to {@link SECURE_BASE_URL}.
 */
export default function useRedirectToPlayer() {
  const { getState } = useAppStore();
  const redirectURLParam = useLatest(useRedirectURLParam());
  const isNewUserFastPlayable = useSelector(getIsNewUserFastPlayable);

  const handleRedirectToPlayer = useCallback((fallbackExternalURL?: string) => {
    const state = getState();

    // MM: we establish where the user should be redirected when this
    // function is called below.
    let redirectURL = new Uri(
      // MM: configRedirectURL is set by rails for gifts/coupons with
      // redirects associated
      getConfigRedirectURL(state) ||
        // WH: goes straight if there is an playable asset on uncredentialed experience
        (isNewUserFastPlayable && fallbackExternalURL) ||
        // MM: launchRedirect is any launch code that has a custom redirect url
        getLaunchRedirect(state) ||
        fallbackExternalURL ||
        // MM: paramsRedirect is the redirect_uri set in the url
        redirectURLParam.current ||
        getDefaultRedirectURL(),
    );
    if (!safeRedirectURL(redirectURL.toString())) {
      redirectURL = new Uri(getDefaultRedirectURL());
    }

    redirectToExternalURL(redirectURL.toString());
  }, []);

  return { handleRedirectToPlayer };
}
